/**
 * Sign up / create account — pre-shell page at `/signup`. Ports the Flutter
 * `auth_page.dart` create-account branch: name, email, password (live NIST
 * checklist), confirm password, job title, and a Privacy-Policy consent
 * checkbox. On submit (mock) it creates a session via `useAuth().signUp` and
 * routes to `/orgs`. Links back to `/login`. Token-driven; no raw hex.
 */
import { useCallback, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useAuth } from '@/app/auth-context'
import { Button, Field, Input } from '@/components/ui'
import { AuthErrorAlert, AuthPaneHeader, AuthShell } from './auth-shell'
import { PasswordInput } from './password-input'
import { PasswordChecklist } from './password-checklist'
import { SsoButton } from './sso-button'
import { validateStrongPassword } from './password-rules'

const PRIVACY_URL = 'https://www.integratrace.com/privacy-policy'

function validateEmail(v: string): string | undefined {
  if (!v.trim()) return 'Email is required'
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())) {
    return 'Enter a valid email address (e.g. you@company.com)'
  }
  return undefined
}

export const SignUpPage = function SignUpPage() {
  return (
    <AuthShell>
      <AuthPaneHeader
        title="Create account"
        subtitle="Fill in your details to create your account."
      />
      <div style={{ marginTop: 'var(--s-6)' }}>
        <SsoButton />
      </div>
      <OrDivider />
      <SignUpForm />
      <p style={{ marginTop: 'var(--s-4)', fontSize: 'var(--fs-13)', color: 'var(--text-mute)' }}>
        Already have an account?{' '}
        <Link to="/login" style={{ color: 'var(--link)', fontWeight: 'var(--fw-medium)' }}>
          Sign in
        </Link>
      </p>
    </AuthShell>
  )
}

function SignUpForm() {
  const { signUp } = useAuth()
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [consent, setConsent] = useState(false)
  // Per-field "touched" so each error surfaces on blur (progressively), not as
  // one wall on submit; submitting reveals everything still unfilled. Mirrors
  // the login form's pattern.
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    password: false,
    confirm: false,
    consent: false,
  })
  const [submitted, setSubmitted] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const markTouched = (field: keyof typeof touched) =>
    setTouched((t) => ({ ...t, [field]: true }))
  const showError = (field: keyof typeof touched) => submitted || touched[field]

  const nameError = showError('name') && !name.trim() ? 'Please enter your name' : undefined
  const emailError = showError('email') ? validateEmail(email) : undefined
  const passwordError = showError('password') ? validateStrongPassword(password) : undefined
  const confirmError =
    showError('confirm') && confirm !== password ? 'Passwords do not match' : undefined
  const consentError =
    showError('consent') && !consent
      ? 'Please read and accept the Privacy Policy to continue'
      : undefined

  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (submitting) return
      setSubmitted(true)
      if (
        !name.trim() ||
        validateEmail(email) ||
        validateStrongPassword(password) ||
        confirm !== password ||
        !consent
      ) {
        return
      }
      setFormError(null)
      setSubmitting(true)
      signUp({ name, email, password, jobTitle })
        // Fresh accounts go through onboarding (create/join an org, then the
        // add-environment step) — not the chooser, which auto-enters any org
        // the session can already see.
        .then(() => navigate({ to: '/orgs/choice' }))
        .catch((err: unknown) => {
          setFormError(err instanceof Error ? err.message : 'Could not create account')
        })
        .finally(() => setSubmitting(false))
    },
    [submitting, name, email, jobTitle, password, confirm, consent, signUp, navigate],
  )

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-4)' }}
    >
      <Input
        label="Name"
        autoComplete="name"
        icon="user"
        placeholder="Ada Lovelace"
        value={name}
        error={nameError}
        onChange={(e) => setName(e.currentTarget.value)}
        onBlur={() => markTouched('name')}
        disabled={submitting}
      />
      <Input
        label="Email"
        type="email"
        autoComplete="email"
        icon="user"
        placeholder="you@company.com"
        value={email}
        error={emailError}
        onChange={(e) => setEmail(e.currentTarget.value)}
        onBlur={() => markTouched('email')}
        disabled={submitting}
      />
      <Input
        label="Job title (optional)"
        autoComplete="organization-title"
        placeholder="Head of Security"
        value={jobTitle}
        onChange={(e) => setJobTitle(e.currentTarget.value)}
        disabled={submitting}
      />
      <PasswordInput
        label="Password"
        autoComplete="new-password"
        value={password}
        error={passwordError}
        onChange={(e) => setPassword(e.currentTarget.value)}
        onBlur={() => markTouched('password')}
        disabled={submitting}
      />
      {password.length > 0 && <PasswordChecklist password={password} />}
      <PasswordInput
        label="Confirm password"
        autoComplete="new-password"
        value={confirm}
        error={confirmError}
        onChange={(e) => setConfirm(e.currentTarget.value)}
        onBlur={() => markTouched('confirm')}
        disabled={submitting}
      />

      <ConsentField
        checked={consent}
        onChange={(next) => {
          setConsent(next)
          markTouched('consent')
        }}
        error={consentError}
        disabled={submitting}
      />

      {formError && <AuthErrorAlert message={formError} />}

      <Button type="submit" size="lg" loading={submitting} disabled={submitting}>
        {submitting ? 'Creating account…' : 'Create account'}
      </Button>
    </form>
  )
}

function ConsentField({
  checked,
  onChange,
  error,
  disabled,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  error?: string
  disabled?: boolean
}) {
  return (
    <Field error={error}>
      {() => (
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--s-2)',
            fontSize: 'var(--fs-13)',
            color: 'var(--text-soft)',
            cursor: disabled ? 'default' : 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange(e.currentTarget.checked)}
            style={{ marginTop: 2, flex: 'none' }}
          />
          <span>
            I have read and agree to the{' '}
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noreferrer"
              style={{ color: 'var(--link)', fontWeight: 'var(--fw-medium)' }}
            >
              Privacy Policy
            </a>{' '}
            and consent to the collection and use of my information.
          </span>
        </label>
      )}
    </Field>
  )
}

function OrDivider() {
  return (
    <div
      aria-hidden="true"
      style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-3)', margin: 'var(--s-5) 0' }}
    >
      <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      <span className="kicker">or</span>
      <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
    </div>
  )
}
